/**
 * CreativePulseIndicator - Indicador do Pulso Criativo
 * 
 * - Posição: canto inferior direito
 * - Estados: azul (fluxo), amarelo (atenção), vermelho (pausa)
 * - Insight leve quando vermelho
 */

import React from 'react';
import { Heart, Zap, Coffee } from 'lucide-react';

const STATES = {
  flow: {
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.2)',
    icon: Zap,
    label: 'Em Fluxo',
    insight: null,
  },
  attention: {
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.2)',
    icon: Heart,
    label: 'Atenção',
    insight: null,
  },
  pause: {
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.2)',
    icon: Coffee,
    label: 'Pausa',
    insight: 'Talvez seja hora de respirar um pouco',
  },
};

export default function CreativePulseIndicator({
  state = 'flow', // 'flow' | 'attention' | 'pause'
  onClick,
}) {
  const currentState = STATES[state] || STATES.flow;
  const Icon = currentState.icon;

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
      {/* Insight message (only for pause state) */}
      {currentState.insight && (
        <div 
          className="px-4 py-2 rounded-lg text-xs text-stone-600 max-w-[200px] text-right"
          style={{
            background: 'rgba(255, 251, 245, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(180, 160, 130, 0.2)',
            animation: 'fadeSlideIn 0.5s ease-out',
          }}
        >
          <span className="text-amber-500 mr-1">✨</span>
          {currentState.insight}
        </div>
      )}

      {/* Pulse indicator */}
      <button
        onClick={onClick}
        className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 hover:scale-105"
        style={{
          background: currentState.bgColor,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${currentState.borderColor}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
        title="Pulso Criativo"
      >
        {/* Icon with pulse animation */}
        <div className="relative">
          <Icon 
            size={18} 
            style={{ color: currentState.color }}
            strokeWidth={2}
          />
          {/* Pulse ring */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${currentState.color}40 0%, transparent 70%)`,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </div>

        {/* Label */}
        <span 
          className="text-xs font-medium"
          style={{ color: currentState.color }}
        >
          {currentState.label}
        </span>
      </button>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.3); }
        }
        @keyframes fadeSlideIn {
          from { 
            opacity: 0;
            transform: translateY(10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
